import { useContext } from "react";
import { DateTime } from "luxon";

import { Context } from "../context";
import { cloudflareImage } from "../utils/utils";
import {
  pageStyle,
  breakpoint1,
  mediumFont,
  largeFont,
  xLargeFont,
  xxLargeFont,
  smallFont,
  tinyFont,
  breakpoint0,
} from "../utils/styles";
import { CheckmarkIcon, NoteIcon, UserCircleIcon } from "../utils/icons";
import SocialShare from "./SocialShare";
import Breadcrumbs from "./Breadcrumbs";

const IMAGE_SIZES = [250, 500, 750, 1000, 1500, 1920];

function makeImageSizes() {
  return IMAGE_SIZES.map((size) => `(max-width: ${size}px) ${size}px`).join(",");
}

function makeImageSrcSet(image, ratio) {
  return IMAGE_SIZES.map(
    (size) =>
      `${cloudflareImage(image, {
        width: size,
        height: parseInt(size / ratio),
        fit: "cover",
      })} ${size}w`
  ).join(",");
}

const ArticleHeader = ({ article }) => {
  const { theme } = useContext(Context);

  const created = DateTime.fromISO(article.created)
    .setZone("America/Vancouver")
    .toFormat("d MMM yyyy h:mm a ZZZZ");
  const modified = DateTime.fromISO(article.modified)
    .setZone("America/Vancouver")
    .toFormat("d MMM yyyy h:mm a ZZZZ");

  const imageRatio = 56 / 9;
  const image = article.image;

  return (
    <div>
      <style jsx>{pageStyle}</style>
      <style jsx>
        {`
          .meta {
            display: grid;
            grid-template-columns: auto;
            width: 100%;
            grid-gap: .5rem;

            border-bottom: 1px solid ${theme.borderColor};
          }
          .meta .line {
            margin-right: 3rem;
            margin-bottom: 1rem;
          }
          .name {
            color: ${theme.accentTextColor};
            cursor: pointer;
          }

          .line {
            display: flex;
            align-items: center;
          }

          .line.updated {
            color: ${theme.accentTextColor};
          }
          .line.updated :global(.icon) {
            fill: ${theme.accentTextColor};
          }
          .line.created {
            color: ${theme.primaryTextColor};
          }
          .line :global(.icon) {
            fill: ${theme.primaryTextColor};
            margin-right: 1rem;
            padding: 0;
            line-height: 2rem;
            width: 2rem;
            height: 2rem;
          }
          .share :global(.icon) {
            width: 2rem;
            height: 2rem;
          }
          .author-name {
            text-decoration: underline;
            text-underline-position: under;
          }
          .top-area {
            position: relative;
            width: 100%;
            background: ${theme.backgroundColor};
          }
          .text-area {
            position: absolute;
            text-align: center;
            width: 100%;
            top: 10%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .tagline {
            font-weight: bold;
            padding: .5rem;
            text-align: center;
            margin-top: "1rem";
            color: white;
            font-size: ${mediumFont};
          }
          .breadcrumbs {
            margin: 1rem 0;
          }
          .mobile-title {
            font-size: ${xLargeFont};
            margin: 1rem 0;
          }
          .mobile-tagline {
            color: ${theme.tertiaryTextColor};
            font-size: 2rem;
            font-style: italic;
            margin-top: 1.5rem;
            margin-bottom: 2.5rem;
            text-align: left;
          }
          @media screen and (min-width: ${breakpoint1}px) {
            .text-area {
              display: block;
            }
            .meta {
              border: none;
            }
            .title {
              font-size: ${xxLargeFont};
              color: ${theme.primaryTextColor};
            }
            .tagline{
              font-size: ${largeFont};
              margin-top: 16px;
              color: ${theme.primaryTextColor};
            }
            .top-area .updated {
              font-size: ${smallFont};
            }
            .top-area .updated :global(.icon) {
              width: 4rem;
              height: 4rem;
              line-height: 4rem;
            }
            .meta {
              grid-template-columns: 1fr 1fr;
            }
            .meta .line.created {
              grid-row: 1;
              grid-column: 2;
            }
            .meta .line.author {
              grid-row: 2;
              grid-column: 1;
            }
            .meta .line.share {
              grid-row: 2;
              grid-column 2;
            }
          }

          .disclaimer {
            text-align: center;
            margin: 1rem auto;
            color: ${theme.tertiaryTextColor};
            font-size: 12px;
          }
        `}
      </style>

      <div className="section">
        <div className="breadcrumbs">
          <Breadcrumbs
            type="article"
            slug={article.slug}
            category={article.category}
            title={article.title}
          />
        </div>
        <h1 className="title">{article.title}</h1>
        <h2 className="tagline">{article.tagline}</h2>
        <div className="meta">
          <div className="line updated">
            <CheckmarkIcon />
            <span>{`Updated: ${modified}`}</span>
          </div>
          {created !== modified && (
            <div className="line created">
              <NoteIcon />
              <span>{`Posted: ${created}`}</span>
            </div>
          )}
          <div className="line author">
            <UserCircleIcon />
            <span className="byline">
              Written by <span className="author-name">{article.author.first_name}</span>
            </span>
          </div>
          <div className="line share">
            <SocialShare text={article.title} image={article.image} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleHeader;
